import { DynamicColumns } from '@/components/tables/crud/DynamicColumns';
import { DataTableColumnType } from '@/components/tables/crud/types';
import { Column } from 'primereact/column';
import {
  DataTableExpandedRows,
  DataTablePageEvent,
  DataTableRowClickEvent,
  DataTableRowEvent,
  DataTable as PrimeDataTable,
} from 'primereact/datatable';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDataTable = styled(PrimeDataTable)<{ hasMultipleSelect?: boolean }>`
  &.p-datatable {
    width: 100%;
    margin: 0 auto;
    text-align: center;
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
    border: 1px solid var(--gray-dark);
    border-radius: var(--medium-border-radius);

    transition: opacity 0.2s ease-in-out;
    transition-delay: 0.2s;

    .is-fetching {
      opacity: 0.5;
      pointer-events: none;
    }

    tr td[role='cell'] div {
      align-items: flex-start;
    }

    th[role='columnheader'] {
      background-color: var(--beige-main) !important;
      border: none !important;
      padding: var(--medium-padding) !important;
      font-size: var(--xsmall-font-size);
      line-height: var(--xsmall-line-height);

      &:last-child {
        width: 80px !important;
      }

      &:only-child {
        text-align: left !important;
        .p-column-header-content {
          justify-content: flex-start !important;
        }
      }

      &:not(.p-highlight) {
        span[data-pc-section='sort'] {
          display: none;
        }
      }
    }

    td[role='cell'] {
      border: none !important;
      padding: var(--default-padding) var(--medium-padding) !important;
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);

      &:last-child {
        height: 82px !important;
        padding: var(--medium-padding) !important;
      }

      &:only-child {
        text-align: left !important;
      }
    }

    tr {
      font-weight: 500 !important;
      color: var(--black-main) !important;

      &:nth-child(even) {
        border: none !important;
        background-color: var(--beige-main) !important;
      }

      &.p-highlight,
      &.p-highlight:hover {
        background-color: var(--gray-highlight) !important;
      }

      &:not(.p-datatable-row-expansion):not(.p-highlight):hover {
        background-color: var(--orange-pale) !important;
        color: var(--black-main) !important;
      }
    }

    &.rowspan-datatable {
      tr {
        background-color: transparent !important;

        td[role='cell'] {
          border-bottom: 1px solid var(--gray-opacity) !important;
          padding: var(--medium-padding) !important;
        }

        td[rowspan] {
          vertical-align: top;
        }

        &:not(.p-datatable-row-expansion):hover {
          background-color: transparent !important;
        }
      }
    }

    .p-datatable-row-expansion > td {
      background-color: var(--white-main) !important;
    }

    .p-datatable-wrapper.hide-scrollbar {
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    &.p-datatable-gridlines .p-datatable-thead > tr > th,
    &.p-datatable-gridlines .p-datatable-tbody > tr > td {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      padding: 10px;
    }

    .p-datatable-wrapper {
      border: none !important;
      border-radius: 0 0 var(--medium-border-radius) var(--medium-border-radius);
      overflow-x: auto;

      ::-webkit-scrollbar-track {
        margin: 12px;
      }

      .p-datatable-emptymessage td[role='cell']:last-child {
        text-align: center;
        font-size: var(--medium-font-size);
        line-height: var(--medium-line-height);
      }
    }

    &:has(.p-datatable-footer),
    &:has(.p-paginator) {
      .p-datatable-wrapper {
        border-radius: 0 !important;
      }
    }

    th.sticky-column,
    td.sticky-column {
      position: sticky !important;
      left: 0;
    }

    th.sticky-column {
      z-index: 2 !important;
      background-color: var(--beige-main) !important;
    }

    td.sticky-column {
      z-index: 1 !important;
      background-color: var(--white-main) !important;
    }

    tr:nth-child(even) td.sticky-column {
      background-color: var(--beige-main) !important;
    }

    tr:not(.p-datatable-row-expansion):hover td.sticky-column {
      background-color: var(--orange-pale) !important;
    }

    &.rowspan-datatable {
      tr td.sticky-column {
        background-color: var(--white-main) !important;
      }

      tr:hover td.sticky-column {
        background-color: var(--white-main) !important;
      }
    }

    .p-sortable-column:focus {
      box-shadow: var(--box-shadow-red-inset);
    }

    .p-sortable-column.p-highlight,
    .p-sortable-column.p-highlight:not(.p-sortable-disabled):hover {
      color: var(--red-main);
      .p-sortable-column-icon {
        color: var(--red-main);
      }
    }

    .p-datatable-header {
      display: flex;
      padding: var(--medium-padding);
      background-color: var(--white-main);
      border: none !important;
      border-radius: var(--medium-border-radius) var(--medium-border-radius) 0 0;
    }

    .p-datatable-footer {
      border: none !important;
      border-radius: 0 0 var(--medium-border-radius) var(--medium-border-radius);
      display: flex;
      padding: var(--xsmall-padding) var(--medium-padding);
      background-color: var(--white-main);
      line-height: var(--xsmall-line-height);
      font-size: var(--xsmall-font-size);
      font-weight: 400;
      text-align: start;
    }

    .p-paginator {
      padding: var(--medium-padding);
      border: none !important;
      border-radius: 0 0 var(--medium-border-radius) var(--medium-border-radius);
    }

    &:hover {
      cursor: ${(props) => (props.onRowClick ? 'pointer' : 'default')};
    }

    .p-datatable-tbody .p-row-toggler.p-link {
      color: var(--gray-darker);

      &:enabled:hover {
        background: var(--beige-main);
      }
      &:focus {
        box-shadow: var(--box-shadow-red);
      }
    }

    .p-paginator-page {
      font-size: var(--medium-font-size);
      margin: 4px;
    }

    .p-paginator .p-dropdown .p-dropdown-label {
      padding-right: 0;
      line-height: var(--body-font-size);
      height: auto;
    }
  }
`;

type CrudTableProps = {
  id?: string;
  data?: Array<any>;
  isLoading: boolean;
  isFetching?: boolean;
  selectedItems?: any;
  setSelectedItems?: any;
  toolbar?: ReactNode;
  filters?: any;
  columns: DataTableColumnType[];
  visibleColumns?: string[];
  isSelectMultiple?: boolean;
  rows?: number;
  skip?: number;
  onSort?: (event: DataTablePageEvent) => void;
  onPage?: (event: DataTablePageEvent) => void;
  onRowClick?: (event: DataTableRowClickEvent) => void;
  sort?: [string, 'asc' | 'desc'] | null;
  count?: number;
  onRowToggle?: (event: DataTableRowEvent) => void;
  rowExpansionTemplate?: (data: any) => any;
  allowExpansion?: boolean;
  expandedRows?: DataTableExpandedRows[];
  classNames?: string;
  dataKey?: string;
  footer?: ReactNode;
  rowGroupMode?: 'rowspan';
  groupRowsBy?: string;
  tableClassName?: string;
  stickyScrollbar?: boolean;
};

export const DataTable: React.FC<CrudTableProps> = ({
  id,
  data,
  isLoading,
  isFetching,
  selectedItems,
  setSelectedItems,
  toolbar,
  filters,
  rows = 10,
  skip = 10,
  columns,
  visibleColumns,
  onSort,
  sort,
  count,
  onPage,
  onRowClick,
  onRowToggle,
  allowExpansion,
  expandedRows,
  rowExpansionTemplate,
  classNames,
  dataKey,
  footer,
  rowGroupMode,
  groupRowsBy,
  tableClassName,
  stickyScrollbar,
  ...rest
}) => {
  const { t } = useTranslation();
  const skeletonKey = dataKey ?? 'id';
  const skeletonData = useMemo(
    () =>
      Array(9)
        .fill('')
        .map((_, index) => ({
          id: `loading-${index}`,
          [skeletonKey]: `loading-${index}`,
        })),
    [skeletonKey],
  );

  const hasFrozenColumns = useMemo(
    () =>
      columns.some(
        (c) =>
          c.frozen &&
          (!visibleColumns || visibleColumns.includes(c.exportName ?? c.field)),
      ),
    [columns, visibleColumns],
  );

  const outerRef = useRef<HTMLDivElement>(null);
  const scrollbarContainerRef = useRef<HTMLDivElement>(null);
  const scrollbarInnerRef = useRef<HTMLDivElement>(null);
  const [showStickyScrollbar, setShowStickyScrollbar] = useState(false);

  useEffect(() => {
    if (!stickyScrollbar) return;

    const outer = outerRef.current;
    const scrollbarContainer = scrollbarContainerRef.current;
    const scrollbarInner = scrollbarInnerRef.current;
    if (!outer || !scrollbarContainer || !scrollbarInner) return;

    const tableWrapper = outer.querySelector<HTMLElement>(
      '.p-datatable-wrapper',
    );
    if (!tableWrapper) return;

    const syncLayout = () => {
      scrollbarInner.style.width = `${tableWrapper.scrollWidth}px`;

      const stickyThs = Array.from(
        outer.querySelectorAll<HTMLElement>('th.sticky-column'),
      );
      const offsets: number[] = [];
      let cumulative = 0;
      stickyThs.forEach((th) => {
        th.style.left = `${cumulative}px`;
        offsets.push(cumulative);
        cumulative += th.offsetWidth;
      });

      const rows = tableWrapper.querySelectorAll<HTMLElement>('tr');
      rows.forEach((row) => {
        Array.from(
          row.querySelectorAll<HTMLElement>('td.sticky-column'),
        ).forEach((td, i) => {
          if (offsets[i] !== undefined) td.style.left = `${offsets[i]}px`;
        });
      });
    };

    const updateVisibility = () => {
      const hasOverflow = tableWrapper.scrollWidth > tableWrapper.clientWidth;
      if (!hasOverflow) {
        setShowStickyScrollbar(false);
        tableWrapper.classList.remove('hide-scrollbar');
        return;
      }
      const { top: wrapperTop, bottom: wrapperBottom } =
        tableWrapper.getBoundingClientRect();
      const shouldShow =
        wrapperBottom > window.innerHeight &&
        wrapperTop < window.innerHeight &&
        wrapperBottom > 0;
      setShowStickyScrollbar(shouldShow);
      if (shouldShow) {
        tableWrapper.classList.add('hide-scrollbar');
      } else {
        tableWrapper.classList.remove('hide-scrollbar');
      }
    };

    syncLayout();
    updateVisibility();

    const onTableScroll = () => {
      scrollbarContainer.scrollLeft = tableWrapper.scrollLeft;
    };

    const onScrollbarScroll = () => {
      tableWrapper.scrollLeft = scrollbarContainer.scrollLeft;
    };

    const onWindowScroll = () => updateVisibility();

    tableWrapper.addEventListener('scroll', onTableScroll, { passive: true });
    scrollbarContainer.addEventListener('scroll', onScrollbarScroll, {
      passive: true,
    });
    window.addEventListener('scroll', onWindowScroll, { passive: true });

    const observer = new ResizeObserver(() => {
      syncLayout();
      updateVisibility();
    });
    observer.observe(tableWrapper);

    return () => {
      tableWrapper.removeEventListener('scroll', onTableScroll);
      scrollbarContainer.removeEventListener('scroll', onScrollbarScroll);
      window.removeEventListener('scroll', onWindowScroll);
      observer.disconnect();
    };
  }, [stickyScrollbar, isLoading]);

  const loadingTable = (
    <StyledDataTable
      id={id}
      lazy
      dataKey={skeletonKey}
      value={skeletonData}
      header={toolbar}
      rowHover
      className={classNames}
      tableClassName={tableClassName}
      {...rest}
    >
      {allowExpansion && (
        <Column
          expander
          style={{ width: '5rem' }}
          className={hasFrozenColumns ? 'sticky-column' : undefined}
          headerClassName={hasFrozenColumns ? 'sticky-column' : undefined}
        />
      )}
      {DynamicColumns({ columns, visibleColumns, isLoading: true })}
    </StyledDataTable>
  );

  const hasSelection = !!setSelectedItems;

  const dataTable = (
    <StyledDataTable
      id={id}
      lazy
      dataKey={dataKey ?? 'id'}
      rowGroupMode={rowGroupMode}
      groupRowsBy={groupRowsBy}
      value={data}
      header={toolbar}
      selection={hasSelection ? selectedItems : undefined}
      onSelectionChange={
        hasSelection
          ? (e: { value: any }) => setSelectedItems?.(e.value)
          : undefined
      }
      selectionMode={hasSelection ? 'checkbox' : null}
      filters={filters}
      onSort={onSort}
      rowHover
      emptyMessage={t('filter.noItems')}
      paginator={data && !!onPage}
      paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      currentPageReportTemplate={`{first} ${t('filter.to')} {last} ${t(
        'filter.of',
      )} {totalRecords}`}
      paginatorClassName="justify-content-end"
      totalRecords={count}
      rows={rows}
      first={skip}
      pageLinkSize={3}
      onPage={onPage}
      onRowClick={onRowClick}
      rowsPerPageOptions={[5, 10, 20, 50, 100]}
      sortField={sort ? sort[0] : undefined}
      sortOrder={sort ? (sort[1] === 'asc' ? 1 : -1) : undefined}
      onRowToggle={onRowToggle}
      expandedRows={expandedRows}
      rowExpansionTemplate={rowExpansionTemplate}
      className={classNames}
      tableClassName={`${tableClassName}${isFetching ? ' is-fetching' : ''}`}
      footer={footer}
      {...rest}
    >
      {allowExpansion && (
        <Column
          expander
          style={{ width: '5rem' }}
          className={hasFrozenColumns ? 'sticky-column' : undefined}
          headerClassName={hasFrozenColumns ? 'sticky-column' : undefined}
        />
      )}
      {hasSelection && (
        <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
      )}
      {DynamicColumns({
        columns,
        visibleColumns,
        isLoading,
      })}
    </StyledDataTable>
  );

  const tableContent = isLoading ? loadingTable : dataTable;

  if (!stickyScrollbar) {
    return tableContent;
  }

  return (
    <div ref={outerRef} style={{ width: '100%' }}>
      {tableContent}
      <div
        ref={scrollbarContainerRef}
        style={{
          position: 'sticky',
          bottom: -24,
          zIndex: 3,
          overflowX: showStickyScrollbar ? 'auto' : 'hidden',
          overflowY: 'hidden',
          height: showStickyScrollbar ? '32px' : '0',
          backgroundColor: 'var(--white-main)',
          transform: 'scaleY(-1)',
        }}
      >
        <div
          ref={scrollbarInnerRef}
          style={{ height: '1px', transform: 'scaleY(-1)' }}
        />
      </div>
    </div>
  );
};
